//
// # Connection Dots
//

// Workspace Imports
import { Point, SolderDot } from "GenAlphaModel";
// Local Imports
import * as style from "../../style";
import { Parent, Entity } from "../../tree";
import { EntityKind } from "../../entity";
import { DrawTo } from "../../draw";

// # Connection `Dot`
//
// The `Dot` class just looks like a circle in our drawing.
// In our editor it essentially does "reference counting"
// among things it may be connecting: wireSegments, instances, and ports.
//
// Dots keep a set of references to their parents, and their parents
// keep references to each Dot. When a parent moves or otherwise no longer
// "uses" the Dot, it removes the Dot from its set of children, and calls
// the associated `remove` method on the Dot.
//
// When a Dot detects that it is only "connecting" a single parent, it
// essentially "garbage collects" itself, removing itself, from
// the remaining parent and from the canvas.
// Note it *does not* remove itself from the `Schematic`.
//
export class Dot extends Entity<SolderDot> {
  readonly entityKind = EntityKind.Dot;
  override readonly cache =
    this.makeCache<style.ShapeStyle>(dotShapeStyleTable);
  readonly parents = new Set<DotParent>();

  // Create a default-sized "lil" dot from a center coordinate
  static fromCenter(center: Point, parent: Parent): Dot {
    const data = SolderDot.new(center, SolderDot.LIL_RADIUS);
    return new Dot({ data, parent });
  }

  get center(): Point {
    return this.data.center;
  }

  redraw = (to: DrawTo): unknown => {
    return to.ellipse(SolderDot.ellipse(this.data), this.cache.style);
  };

  // Check if we are connecting anything, and if not, remove ourselves.
  // Generally should be called after removing a parent.
  maybeRemove = () => {
    if (this.refCount() <= 1) this.remove();
  };

  // Remove this dot from its parents and from the GUI tree.
  //
  // Dots inferred by `Dots.infer` have no `parents` — nothing populates that
  // set — so this used to do nothing at all, while the `Entity` constructor
  // had already added each one to its parent's `children`. Every recompute
  // therefore leaked a generation of dots into the tree, which `highlight` and
  // `invalidate` then walked.
  remove = () => {
    this.parents.forEach((parent) => parent.dots.delete(this));
    this.parent.children.delete(this);
  };

  // Calculate our "reference count", i.e. the number of things attached to us.
  refCount = (): number => this.parents.size;
}

export class Dots extends Set<Dot> {}

// Interface for "Dot Parents",
// i.e. schematic entities which have dots as children.
export interface DotParent {
  dots: Dots;
}

const wireColors: [style.Color, style.Color, style.Color, style.Color] = [
  style.Color.blue,
  style.Color.red,
  style.Color.new(37, 150, 190),
  style.Color.red,
];

const dotShapeStyleTable: style.ShapeStyleTable = {
  //       light light    dark  dark
  //       no    hilited  no    hilited
  fill: wireColors,
  cap: "round",
  join: "round",
  // No stroke for dots
  stroke: style.Special.None,
  linewidth: 0,
};
